<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div v-if="visible" class="dsc" dir="rtl">
        <div class="dsc__backdrop" @click="dismiss"></div>

        <div class="dsc__sheet">

          <!-- Header -->
          <div class="dsc__header">
            <div class="dsc__icon-wrap">
              <i class="fa fa-share-nodes"></i>
            </div>
            <div>
              <h2 class="dsc__title">طلب مشاركة بيانات</h2>
              <p class="dsc__from">من: <strong>{{ consent.third_party_name }}</strong></p>
            </div>
            <button class="dsc__dismiss" @click="dismiss" type="button">
              <i class="fa fa-xmark"></i>
            </button>
          </div>

          <!-- Purpose -->
          <div class="dsc__body">
            <div class="dsc__row">
              <span class="dsc__row-label">الغرض</span>
              <span class="dsc__row-value">{{ consent.purpose }}</span>
            </div>

            <div v-if="consent.description" class="dsc__desc">
              {{ consent.description }}
            </div>

            <!-- Data types -->
            <div class="dsc__types-head">البيانات المطلوبة:</div>
            <div class="dsc__types">
              <span
                v-for="type in consent.data_types"
                :key="type"
                class="dsc__type-chip"
              >{{ type }}</span>
            </div>

            <div v-if="consent.expires_at" class="dsc__expiry">
              <i class="fa fa-clock"></i>
              تنتهي صلاحية هذا الطلب في {{ consent.expires_at }}
            </div>
          </div>

          <!-- Actions -->
          <div class="dsc__footer">
            <p class="dsc__notice">
             إذا رفضت، لن تُشارَك بياناتك مع هذا الطرف.
            </p>
            <div class="dsc__btns">
              <button
                class="dsc__btn dsc__btn--refuse"
                @click="respond('refused')"
                :disabled="submitting"
                type="button"
              >
                <i class="fa fa-times"></i>
                رفض
              </button>
              <button
                class="dsc__btn dsc__btn--accept"
                @click="respond('accepted')"
                :disabled="submitting"
                type="button"
              >
                <i class="fa" :class="submitting ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                موافق
              </button>
            </div>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref } from "vue";
import axios from "axios";

const props = defineProps({
  consent: { type: Object, required: true },
});
const emit = defineEmits(["responded", "dismissed"]);

const apiBase   = process.env.VUE_APP_API_URL || "";
const submitting = ref(false);
const visible    = ref(true);

async function respond(response) {
  submitting.value = true;
  try {
    const token = localStorage.getItem("token");
    await axios.post(
      `${apiBase}/policy/consent/respond`,
      { consent_id: props.consent.consent_id, response },
      { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
    );
    visible.value = false;
    emit("responded", { consentId: props.consent.consent_id, response });
  } catch (e) {
    console.error("Consent respond failed:", e);
  } finally {
    submitting.value = false;
  }
}

function dismiss() {
  // Dismiss without responding — stays pending, will show again next session
  visible.value = false;
  emit("dismissed", props.consent.consent_id);
}
</script>

<style scoped lang="scss">
.dsc {
  position: fixed;
  inset: 0;
  z-index: 1100; // above Bootstrap .fixed-bottom (1030) and AppFooter
  display: flex;
  align-items: flex-end;
  direction: rtl;

  &__backdrop {
    position: absolute; inset: 0;
    background: rgba(4, 6, 12, 0.60);
    backdrop-filter: blur(4px);
  }

  &__sheet {
    position: relative; z-index: 1;
    width: 100%; max-width: var(--app-max-width); margin: 0 auto;
    background: var(--color-bg);
    border-radius: 22px 22px 0 0;
    overflow: hidden;
    // Push content above the AppFooter (fixed-bottom ~70px + safe area)
    padding-bottom: max(72px, calc(70px + env(safe-area-inset-bottom, 0px)));
  }

  // ── Header ──────────────────────────────────
  &__header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    padding: 18px var(--page-padding-x) 12px;
    border-bottom: 1px solid var(--color-border);
  }

  &__icon-wrap {
    width: 40px; height: 40px; flex-shrink: 0;
    border-radius: var(--radius-md);
    background: var(--color-signal-soft);
    border: 1px solid var(--color-signal-glow);
    display: flex; align-items: center; justify-content: center;
    color: var(--color-signal); font-size: 1rem;
  }

  &__title {
    margin: 0 0 2px;
    font-size: 0.95rem; font-weight: 800; color: var(--color-text-1);
  }

  &__from {
    margin: 0;
    font-size: 0.75rem; color: var(--color-text-3);
    strong { color: var(--color-text-1); }
  }

  &__dismiss {
    margin-right: auto; flex-shrink: 0;
    width: 32px; height: 32px; border-radius: 50%;
    border: 1px solid var(--color-border);
    background: var(--color-card); color: var(--color-text-3);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
    &:hover { border-color: var(--color-signal); color: var(--color-signal); }
  }

  // ── Body ────────────────────────────────────
  &__body {
    padding: var(--space-3) var(--page-padding-x);
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-3);
    margin-bottom: var(--space-2);

    &-label { font-size: 0.75rem; font-weight: 700; color: var(--color-text-4); white-space: nowrap; }
    &-value { font-size: 0.82rem; color: var(--color-text-1); text-align: left; }
  }

  &__desc {
    font-size: 0.78rem;
    color: var(--color-text-3);
    line-height: 1.6;
    margin-bottom: var(--space-3);
    padding: var(--space-2) var(--space-3);
    background: var(--color-panel);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
  }

  &__types-head {
    font-size: 0.72rem; font-weight: 700;
    color: var(--color-text-3); margin-bottom: 8px;
  }

  &__types {
    display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: var(--space-3);
  }

  &__type-chip {
    display: inline-flex; align-items: center;
    padding: 3px 10px; border-radius: var(--radius-full);
    background: var(--color-panel); border: 1px solid var(--color-border);
    font-size: 0.72rem; color: var(--color-text-2); font-weight: 500;
  }

  &__expiry {
    font-size: 0.72rem; color: var(--color-text-4);
    display: flex; align-items: center; gap: 5px;
    i { font-size: 0.65rem; }
  }

  // ── Footer ──────────────────────────────────
  &__footer {
    padding: var(--space-2) var(--page-padding-x);
    padding-bottom: max(var(--space-3), env(safe-area-inset-bottom, 12px));
    border-top: 1px solid var(--color-border);
  }

  &__notice {
    margin: 0 0 var(--space-3);
    font-size: 0.72rem;
    color: var(--color-text-4);
    line-height: 1.6;
    text-align: center;
  }

  &__btns {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: var(--space-2);
  }

  &__btn {
    display: flex; align-items: center; justify-content: center;
    gap: 6px; height: 48px;
    border-radius: var(--radius-lg); border: 1px solid;
    font-family: var(--font-primary); font-size: 0.88rem;
    font-weight: 700; cursor: pointer; transition: all 0.15s;
    &:disabled { opacity: 0.45; cursor: not-allowed; }

    &--refuse {
      background: var(--color-card);
      border-color: var(--color-border-lit);
      color: var(--color-text-2);
      &:hover:not(:disabled) { border-color: #ef4444; color: #ef4444; }
    }

    &--accept {
      background: linear-gradient(135deg, var(--color-signal), #1a7fd4);
      border-color: transparent;
      color: #fff;
      box-shadow: var(--shadow-signal);
      &:hover:not(:disabled) { opacity: 0.9; }
    }
  }
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-up-enter-from, .slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>